/**
 * Copyright (c) 2022 Yoshinobu Obata
 * Released under the Apache-2.0 license
 * LICENSE: http://www.apache.org/licenses/LICENSE-2.0
 */
const ArgumentType = require("../../extension-support/argument-type");
const Cast = require("../../util/cast");
const BlockType = require("../../extension-support/block-type");
const formatMessage = require("format-message");
const log = require('../../util/log');

const { Message, AvailableLocales } = require('./config');
const EmotionModule = require('./emotion');
const TranslationModule = require('./translation');
const {
    getSharedModule,
    hasSharedModule,
    isTransformersLoaded,
    isTransformersLoading,
    setTransformersState,
    waitForTransformers
} = require('./shared');

// 共有翻訳モジュールの名前
const TRANSLATION_MODULE_NAME = 'translationModule';

class kurikitEmotionBlocks {
    constructor(runtime) {
        this.runtime = runtime;
        
        // 感情分析モジュールの初期化
        this.emotion = new EmotionModule();
        
        // 翻訳モジュールは共有から取得するか、なければ作成
        this.translation = null;
        
        // Transformers.jsをロードし、モデルを初期化
        this._initializeExtension();
    }

    /**
     * 翻訳モジュールを取得（共有優先）
     */
    _getTranslationModule() {
        // 既にローカルで持っている場合はそれを返す
        if (this.translation) {
            return this.translation;
        }
        
        // 共有モジュールを確認
        if (hasSharedModule(TRANSLATION_MODULE_NAME)) {
            this.translation = getSharedModule(TRANSLATION_MODULE_NAME);
            log.log('Using shared translation module');
            return this.translation;
        }
        
        return null;
    }

    /**
     * 翻訳モジュールを作成（共有がない場合のフォールバック）
     */
    _createTranslationModule() {
        if (!this.translation) {
            log.log('Creating translation module (shared not available)');
            this.translation = new TranslationModule();
        }
        return this.translation;
    }

    async _initializeExtension() {
        try {
            // Transformers.jsをロード
            await this._loadTransformersJS();
            
            // 共有翻訳モジュールを確認
            let translationModule = this._getTranslationModule();
            
            if (translationModule) {
                log.log('Found shared translation module, status:', translationModule.getStatus());
                // 共有モジュールが準備完了していない場合は待機
                if (translationModule.getStatus() === 'loading') {
                    log.log('Waiting for shared translation module to be ready...');
                    await this._waitForTranslationReady(translationModule);
                }
            } else {
                // 共有モジュールがない場合は自分で作成してロード
                log.log('No shared translation module found, creating own instance...');
                translationModule = this._createTranslationModule();
                await translationModule.initialize();
            }
            
            log.log('Translation module ready, now loading emotion model...');
            
            // 翻訳モデルロード後に感情分析モデルを自動ロード
            await this.emotion.initialize();
            
            log.log('Emotion extension initialized successfully');
        } catch (error) {
            log.error('Failed to initialize emotion extension:', error);
        }
    }

    /**
     * 翻訳モジュールの準備完了を待つ
     */
    async _waitForTranslationReady(translationModule) {
        return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
                const status = translationModule.getStatus();
                if (status === 'ready' || status === 'error') {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);
        });
    }

    async _loadTransformersJS() {
        try {
            // 既にロード済みの場合はスキップ
            if (isTransformersLoaded()) {
                log.log('Transformers.js already loaded');
                return;
            }

            // 他の拡張機能がロード中の場合は待機
            if (isTransformersLoading()) {
                log.log('Waiting for Transformers.js to be loaded by another extension...');
                await waitForTransformers();
                return;
            }

            // ロード開始を宣言
            setTransformersState('loading');

            const script = document.createElement('script');
            script.type = 'module';
            script.textContent = `
                import { pipeline, env } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.0.2';
                env.allowLocalModels = false;
                env.useBrowserCache = false;
                window.transformers = { pipeline, env };
            `;
            document.head.appendChild(script);

            await waitForTransformers();

            // ロード完了を宣言
            setTransformersState('loaded');

            log.log('Transformers.js loaded successfully');
        } catch (error) {
            log.error('Failed to load Transformers.js:', error);
        }
    }

    setLocale() {
        let locale = formatMessage.setup().locale;
        if (AvailableLocales.includes(locale)) {
            return locale;
        } else {
            return 'en';
        }
    }

    getInfo() {
        const locale = this.setLocale();
        return {
            id: "kurikitemotion",
            name: Message.categoryName[locale],
            blocks: [
                // {
                //     opcode: "emotionModelStatus",
                //     text: Message.modelStatus[locale],
                //     blockType: BlockType.REPORTER,
                // },
                // {
                //     opcode: "translationModelStatus",
                //     text: Message.translationModelStatus[locale],
                //     blockType: BlockType.REPORTER,
                // },
                // "---",
                {
                    opcode: "emotionAnalysis",
                    text: Message.emotion[locale],
                    blockType: BlockType.REPORTER,
                    arguments: {
                        WORDS: {
                            type: ArgumentType.STRING,
                            defaultValue: "とっても嬉しいです"
                        }
                    },
                },
                {
                    opcode: "emotionCondition",
                    text: Message.emotionCondition[locale],
                    blockType: BlockType.BOOLEAN,
                    arguments: {
                        WORDS: {
                            type: ArgumentType.STRING,
                            defaultValue: "とっても嬉しいです"
                        },
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: 'emotionMenu',
                            defaultValue: 'joy'
                        }
                    },
                },
                {
                    opcode: "emotionScore",
                    text: Message.emotionScore[locale],
                    blockType: BlockType.REPORTER,
                    arguments: {
                        WORDS: {
                            type: ArgumentType.STRING,
                            defaultValue: "とっても嬉しいです"
                        },
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: 'emotionMenu',
                            defaultValue: 'joy'
                        }
                    },
                },
            ],
            menus: {
                emotionMenu: {
                    acceptReporters: false,
                    items: [
                        { text: Message.emotionMenu.joy[locale], value: 'joy' },
                        { text: Message.emotionMenu.sadness[locale], value: 'sadness' },
                        { text: Message.emotionMenu.anger[locale], value: 'anger' },
                        { text: Message.emotionMenu.fear[locale], value: 'fear' },
                        { text: Message.emotionMenu.surprise[locale], value: 'surprise' },
                        { text: Message.emotionMenu.disgust[locale], value: 'disgust' },
                        { text: Message.emotionMenu.neutral[locale], value: 'neutral' }
                    ]
                }
            }
        };
    }

    emotionModelStatus() {
        const status = this.emotion.getStatus();
        const locale = this.setLocale();
        
        switch(status) {
            case 'loading':
                return Message.modelLoading[locale];
            case 'ready':
                return Message.modelReady[locale];
            case 'error':
                return Message.modelError[locale];
            default:
                return Message.modelNotReady[locale];
        }
    }

    translationModelStatus() {
        // 翻訳モジュールを取得（共有または自身）
        const translationModule = this._getTranslationModule();
        const locale = this.setLocale();
        
        if (!translationModule) {
            return Message.modelNotReady[locale];
        }
        
        const status = translationModule.getStatus();
        
        switch(status) {
            case 'loading':
                return Message.modelLoading[locale];
            case 'ready':
                return Message.modelReady[locale];
            case 'error':
                return Message.modelError[locale];
            default:
                return Message.modelNotReady[locale];
        }
    }

    /**
     * 感情ラベルをロケールに応じて変換
     * @param {string} emotion - 英語の感情ラベル (joy, sadness, etc.)
     * @returns {string} ロケールに応じた感情ラベル
     */
    _localizeEmotion(emotion) {
        const locale = this.setLocale();
        
        // emotionMenuから対応するラベルを取得
        if (Message.emotionMenu[emotion] && Message.emotionMenu[emotion][locale]) {
            return Message.emotionMenu[emotion][locale];
        }
        
        // 見つからない場合は元の値を返す
        return emotion;
    }

    async emotionAnalysis(args) {
        const text = Cast.toString(args.WORDS);
        const locale = this.setLocale();
        
        if (!text || text.trim() === '') {
            return this._localizeEmotion('neutral');
        }

        if (this.emotion.getStatus() === 'loading') {
            return Message.loadingMessage[locale];
        }
        
        // 翻訳モジュールを取得（なければ作成）
        let translationModule = this._getTranslationModule();
        if (!translationModule) {
            translationModule = this._createTranslationModule();
        }
        
        const emotion = await this.emotion.analyze(text, translationModule);
        return this._localizeEmotion(emotion);
    }

    async emotionCondition(args) {
        const text = Cast.toString(args.WORDS);
        const targetEmotion = Cast.toString(args.EMOTION);
        
        if (this.emotion.getStatus() === 'loading') {
            return false;
        }

        // 翻訳モジュールを取得（なければ作成）
        let translationModule = this._getTranslationModule();
        if (!translationModule) {
            translationModule = this._createTranslationModule();
        }

        const detectedEmotion = await this.emotion.analyze(text, translationModule);
        return detectedEmotion === targetEmotion;
    }

    async emotionScore(args) {
        const text = Cast.toString(args.WORDS);
        const targetEmotion = Cast.toString(args.EMOTION);
        
        if (!text || text.trim() === '') {
            return 0;
        }

        if (this.emotion.getStatus() === 'loading') {
            return 0;
        }

        // 翻訳モジュールを取得（なければ作成）
        let translationModule = this._getTranslationModule();
        if (!translationModule) {
            translationModule = this._createTranslationModule();
        }

        return await this.emotion.getScore(text, targetEmotion, translationModule);
    }
}

module.exports = kurikitEmotionBlocks;